import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewContainerRef,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import { BehaviorSubject, combineLatest, map, startWith, Subject, takeUntil } from 'rxjs';
import { UnsavedChangesIcon } from '../components/icons/unsaved-changes-icon/unsaved-changes-icon';

@Directive({
  selector: '[appTrackUnsavedField]',
})
export class TrackUnsavedFieldDirective<TValue> implements OnInit, OnDestroy {
  @Input() public reset$?: Subject<unknown>;
  @Input() public trackingIsDisabled = false;
  @Output() public unsavedChanges = new EventEmitter<boolean>();

  private readonly initialValue$ = new BehaviorSubject<TValue | null>(null);
  private readonly destroy$ = new Subject<void>();
  private iconRef?: ComponentRef<UnsavedChangesIcon>;
  private lastState = false;

  private readonly ngControl = inject(NgControl);
  private readonly elementRef = inject(ElementRef<HTMLInputElement>);
  private readonly renderer = inject(Renderer2);
  private readonly vcr = inject(ViewContainerRef);

  public ngOnInit(): void {
    const control = this.ngControl.control;
    if (!control) {
      return;
    }

    this.initialValue$.next(control.value as TValue);

    this.reset$?.pipe(takeUntil(this.destroy$)).subscribe(() => this.initialValue$.next(control.value as TValue));

    combineLatest([control.valueChanges.pipe(startWith(control.value as TValue)), this.initialValue$])
      .pipe(
        map(([current, initial]) => initial !== null && !this.equals(current, initial)),
        takeUntil(this.destroy$)
      )
      .subscribe((changed) => this.updateDom(changed));
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.initialValue$.complete();
    this.destroyIcon();
  }

  private updateDom(changed: boolean): void {
    if (changed === this.lastState || this.trackingIsDisabled) {
      return;
    }
    this.lastState = changed;

    const el = this.elementRef.nativeElement;

    if (changed) {
      this.renderer.addClass(el, 'unsaved-field');
      this.renderer.setAttribute(el, 'aria-invalid', 'true'); // Accessibility (A11y)
      this.renderer.setAttribute(el, 'data-unsaved', 'true');
      this.showIcon();
    } else {
      this.renderer.removeClass(el, 'unsaved-field');
      this.renderer.removeAttribute(el, 'aria-invalid');
      this.renderer.removeAttribute(el, 'data-unsaved');
      this.hideIcon();
    }

    this.unsavedChanges.emit(changed);
  }

  private showIcon(): void {
    if (this.iconRef) return;

    this.iconRef = this.vcr.createComponent(UnsavedChangesIcon);
  }

  private hideIcon(): void {
    this.destroyIcon();
  }

  private destroyIcon(): void {
    this.iconRef?.destroy();
    this.iconRef = undefined;
    this.vcr.clear();
  }

  private equals(a: TValue, b: TValue): boolean {
    return JSON.stringify(a) === JSON.stringify(b);
  }
}
